import React, { useEffect, useState } from 'react';
import {
  Action,
  type AuthEventEmitter,
  AuthFailureType,
  AuthStatus,
  AuthType,
  RuntimeFilterOp,
  init,
} from '@thoughtspot/visual-embed-sdk';
// eslint-disable-next-line import/no-unresolved -- the node resolver can't read the package's `exports` map; tsc and Vite resolve it
import { LiveboardEmbed } from '@thoughtspot/visual-embed-sdk/react';
import { IconButton, Skeleton, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';
import { Box } from '@mui/system';
import { getContractCountAction } from 'src/redux/actions/getContractCountAction';
import { thoughtSpotUrl, thoughtSpotApiUrl, thoughtSpotAuthType } from 'src/config';
import FallbackMessage from 'src/components/common-modals/fallback-message';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { FallbackMessages } from './fallBackMessages';

type ThoughtSpotConsumer = 'therapist' | 'dor' | 'mds';

/**
 * Which `parameter_values` entry of the auth-service reply carries each consumer's liveboard id.
 * The reply lists every board the tenant's ThoughtSpot deployment has; a deployment that predates
 * a board (RA-2134 added MDS_REPORTING) simply lacks that key.
 */
const LIVEBOARD_KEY: Record<ThoughtSpotConsumer, string> = {
  therapist: 'THERAPIST_REPORTING',
  dor: 'DOR_REPORTING',
  mds: 'MDS_REPORTING',
};

const fetchData = async () => {
  const __ttData = await getAccessToken();
  const realm = getRealmName();
  const tsUrl = thoughtSpotUrl;

  if (realm && __ttData) {
    const response = await fetch(`${tsUrl}/thoughtspot/auth/${realm}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${__ttData}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    return data;
  } else return '';
};

const isInvalidTokenString = (token: unknown): boolean => {
  if (typeof token !== 'string' || !token) return true;
  try {
    const parsed = JSON.parse(token);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
};

let authEE: AuthEventEmitter | null = null;

const initThoughtSpot = (useCookieless: boolean): AuthEventEmitter | null => {
  if (authEE) return authEE;

  const realm = getRealmName();
  if (!realm) return null;

  authEE = init({
    thoughtSpotHost: thoughtSpotApiUrl,
    authType: useCookieless ? AuthType.TrustedAuthTokenCookieless : AuthType.TrustedAuthToken,
    autoLogin: true,
    username: realm,
    getAuthToken: async (): Promise<string> => {
      const freshData = await fetchData();
      if (!freshData?.token || isInvalidTokenString(freshData.token)) {
        throw new Error('Failed to obtain a valid ThoughtSpot auth token');
      }
      return freshData.token;
    },
  });
  return authEE;
};

const ThoughtSpotIntegration = ({ who = 'therapist' }: { who: ThoughtSpotConsumer }) => {
  const [data, setData] = useState<any>(null);
  const dispatch = useDispatch();
  const [isAuthenticate, setIsAuthenticate] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  // When off (or while Split is still loading), fall back to the legacy cookie-based flow.
  const [isCookielessAuthEnabled] = useFeatureToggleWhenReady(thoughtSpotAuthType, 'ENTERPRISE');
  // RA-2135: the contract banner is about the profit-and-loss reports, which the MDS Metrics board
  // does not carry — its permission is catalogued as "the MDS figures without the organisation's
  // financial insights". So the MDS consumer neither asks for the contract count nor shows the
  // banner; therapist and DOR are unchanged.
  const showsFinancials = who !== 'mds';
  // const [hasLiveBoardError, setHasLiveBoardError] = useState(false);
  const therapistUserName = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.userName
  );

  const facilityId = localStorage.getItem('facilityId');

  //Validating that the facility stored in local storage is one of the assigned facilities of the user
  const relevantFacilities = useSelector(
    (state: any) => state?.getFacilityDropDownReducer?.response?.data?.data
  );
  let validFacility = false;
  if (relevantFacilities && relevantFacilities.length > 0) {
    for (let i = 0; i < relevantFacilities.length; i++) {
      if (relevantFacilities[i].uuid === facilityId) {
        validFacility = true;
      }
    }
  }

  const contractCount = useSelector((state: any) => state.getContractCountReducer?.response?.data);

  const [bannerText, setBannerText] = useState(
    'In order to produce data in your profit and loss report, you need to configure your contracts in the Enterprise Portal.'
  );

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (contractCount?.finalCount === 0 && contractCount?.inactiveCount === 0) {
      setBannerText(
        'In order to produce data in your profit and loss report, you need to configure your contracts in the Enterprise Portal.'
      );
      setShowBanner(true);
    } else if (contractCount?.finalCount === 0 && contractCount?.inactiveCount > 0) {
      setBannerText(
        'All of your contracts have terminated. In order to produce data in your profit and loss and margin reports, you need to configure new contracts in the Enterprise Portal.'
      );
      setShowBanner(true);
    }
  }, [contractCount]);

  function isValidJSONString(str: string): boolean {
    try {
      const parsed = JSON.parse(str);
      return typeof parsed === 'object' && parsed !== null;
    } catch (e) {
      return false;
    }
  }

  const handleRefresh = () => {
    setIsAuthenticate(true);
    setIsLoading(true);
    setData(null);
    // setHasLiveBoardError(false);
    // Retry fetching data
    const fetchToken = async () => {
      try {
        const data = await fetchData();
        if (isValidJSONString(data?.token)) throw new Error('Invalid token');
        if (data?.detail === 'Could not validate credentials') setIsAuthenticate(false);
        setData(data);
        initThoughtSpot(isCookielessAuthEnabled === true);
      } catch (error) {
        setIsAuthenticate(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchToken();
  };

  useEffect(() => {
    // Wait for Split.io to resolve the flag before initializing the SDK,
    // so we don't init with the wrong AuthType and then have to re-init.
    if (isCookielessAuthEnabled === null) return;

    let onFailure: ((failureType: AuthFailureType) => void) | null = null;
    let ee: AuthEventEmitter | null = null;

    const fetchToken = async () => {
      try {
        setIsLoading(true);
        const data = await fetchData();
        if (isValidJSONString(data?.token)) throw new Error('Invalid token');

        if (data?.detail === 'Could not validate credentials') setIsAuthenticate(false);
        setData(data);
        ee = initThoughtSpot(isCookielessAuthEnabled === true);
        if (ee) {
          onFailure = (failureType: AuthFailureType) => {
            if (failureType === AuthFailureType.EXPIRY) return;
            console.log('Error under fetchtoken method for LiveBoard: ', failureType);
            setIsAuthenticate(false);
          };
          ee.on(AuthStatus.FAILURE, onFailure);
        }
      } catch (error) {
        setIsAuthenticate(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchToken();
    if (showsFinancials) dispatch(getContractCountAction());

    return () => {
      if (ee && onFailure) {
        ee.off(AuthStatus.FAILURE, onFailure, undefined, false);
      }
    };
  }, [isCookielessAuthEnabled, showsFinancials]);

  const runtimeFilters =
    who === 'therapist'
      ? [
          {
            columnName: 'User Name',
            operator: RuntimeFilterOp.EQ,
            // TO DO:
            // The value below will need to be a parameter passed in based on who is logged in once embedded in Insights, not hardcoded
            values: therapistUserName ? [therapistUserName] : ['helpy123'],
          },
        ]
      : [];

  // Every consumer is scoped to the selected facility (RA-2135 added `mds`, filtered like `dor`).
  if (validFacility) {
    runtimeFilters.push({
      columnName: 'Facility ID',
      operator: RuntimeFilterOp.EQ,
      values: [facilityId],
    });
  } else {
    return <Typography variant="h5">No Facility Selected</Typography>;
  }

  // Show fallback message if authentication fails, LiveBoard fails to load
  if (!isAuthenticate || (!isLoading && !data)) {
    return (
      <FallbackMessage
        title={FallbackMessages[who].title}
        message={FallbackMessages[who].message}
        onRefresh={handleRefresh}
      />
    );
  }

  // Undefined when the reply has no `parameter_values` at all, or lists other boards but not this
  // consumer's — either way there is nothing to embed and the fallback below takes over.
  const liveboardId: string | undefined = data?.parameter_values?.[LIVEBOARD_KEY[who]];

  return (
    <>
      {showsFinancials && showBanner && (
        <Box
          sx={{
            display: 'flex',
            border: '1px solid #00000042',
            background: '#dc2626',
            minHeight: '60px',
            color: '#ffffff',
            padding: '5px 10px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              fontSize: 18,
            }}
          >
            {bannerText}
          </Typography>

          <IconButton
            onClick={() => {
              setShowBanner(false);
            }}
          >
            <CancelIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
      )}

      {liveboardId ? (
        <LiveboardEmbed
          frameParams={{
            width: '100%',
            height: '90vh',
          }}
          // fullHeight={true}
          liveboardId={liveboardId}
          isLiveboardHeaderSticky={false}
          // visibleActions={[Action.DownloadAsPdf]}
          hideLiveboardHeader={false}
          isLiveboardCompactHeaderEnabled={true}
          hideIrrelevantChipsInLiveboardTabs={true}
          hiddenActions={[
            Action.RequestAccess,
            Action.AddToFavorites,
            Action.Share,
            Action.Edit,
            Action.MakeACopy,
            Action.RenameModalTitleDescription,
            Action.Present,
            Action.Schedule,
            Action.SchedulesList,
            Action.ExportTML,
            Action.RequestVerification,
            Action.LiveboardInfo,
            Action.CreateMonitor,
            Action.Pin,
            Action.ShowUnderlyingData,
            Action.CopyLink,
            Action.ExportTML,
            Action.ImportTML,
            Action.UpdateTML,
            Action.EditTML,
            Action.SyncToOtherApps,
            Action.ChangeFilterVisibilityInTab,
          ]}
          // onInit={(e) => !refresh && setRefresh(true)}
          runtimeFilters={runtimeFilters}
          onError={(e) => {
            // ToDo: Check error reason from "e"
            // setHasLiveBoardError(true);
            console.log('Error from LiveBoard : ThoughtspotIntegration.tsx', e);
          }}
        />
      ) : isLoading ? (
        <>
          <Skeleton height={30} />
          <Skeleton
            height={30}
            animation="wave"
          />
          <Skeleton
            height={30}
            animation={false}
          />
        </>
      ) : (
        <FallbackMessage
          title={FallbackMessages[who].title}
          message={FallbackMessages[who].message}
          onRefresh={handleRefresh}
        />
      )}
    </>
  );
};

export default ThoughtSpotIntegration;
